import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, NavigationEnd } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { Observable, BehaviorSubject } from 'rxjs';

interface Breadcrumb {
  label: string;
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private readonly BREADCRUMB_KEY = 'breadcrumbs';
  private breadcrumbsSubject: BehaviorSubject<Breadcrumb[]> =
    new BehaviorSubject<Breadcrumb[]>(this.getBreadcrumbsFromLocalStorage());

  constructor(private router: Router) {
    this.subscribeToRouterEvents();
  }

  private subscribeToRouterEvents(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          const route = this.router.routerState.snapshot.root;
          const breadcrumbs = this.buildBreadcrumbs(route);
          this.saveBreadcrumbsToLocalStorage(breadcrumbs);
          this.breadcrumbsSubject.next(this.filterBreadcrumbs(breadcrumbs));
          return breadcrumbs;
        })
      )
      .subscribe();
  }

  private buildBreadcrumbs(
    route: ActivatedRouteSnapshot,
    url: string = '',
    breadcrumbs: Breadcrumb[] = []
  ): Breadcrumb[] {
    const routeConfig = route.routeConfig;
    let breadcrumbUrl;
    if (routeConfig && routeConfig.data) {
      const breadcrumbLabel = routeConfig.data['breadCrum'];
      breadcrumbUrl = `${url}/${route.url
        .map((segment) => segment.path)
        .join('/')}`;
      if (breadcrumbLabel) {
        breadcrumbs.push({ label: breadcrumbLabel, url: breadcrumbUrl });
      }
    }

    if (route.firstChild) {
      return this.buildBreadcrumbs(
        route.firstChild,
        breadcrumbUrl,
        breadcrumbs
      );
    }

    return breadcrumbs;
  }

  private filterBreadcrumbs(breadcrumbs: Breadcrumb[]): Breadcrumb[] {
    return breadcrumbs.filter(
      (breadcrumb) => breadcrumb.label.toLowerCase() !== 'default'
    );
  }

  private saveBreadcrumbsToLocalStorage(breadcrumbs: Breadcrumb[]): void {
    localStorage.setItem(this.BREADCRUMB_KEY, JSON.stringify(breadcrumbs));
  }

  private getBreadcrumbsFromLocalStorage(): Breadcrumb[] {
    const storedBreadcrumbs = localStorage.getItem(this.BREADCRUMB_KEY);
    return storedBreadcrumbs ? JSON.parse(storedBreadcrumbs) : [];
  }

  public getBreadcrumbs(): Observable<Breadcrumb[]> {
    return this.breadcrumbsSubject.asObservable();
  }
}
